import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AccordionComponent,
  AccordionItemComponent,
  Dropdown,
  DropdownOption,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  FormField,
  Input,
  LoadingStateComponent,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  type ProfileDangerAction,
  type ProfileQuickAction,
  type ProfileStat,
  ConfirmDialogService,
  ToastService,
  ZagButton,
  ZagIcon,
  getFieldError as getFieldErrorUtil,
  hasAuditLogAccess,
  SidebarNavStateService,
  wireAuditPanelAutoOpen,
} from '@zag/ui';
import { AuditLogPanelComponent } from '@zag/core/ui';
import { RolesManagementService, type RoleUserSummary } from '../roles-management.service';
import { PermissionsManagementService } from '../permissions-management.service';
import { PermissionChecklistComponent, type PermissionChecklistItem } from '../permission-checklist/permission-checklist.component';
import type { PermissionScope, PermissionScopeSelection } from '../permission-checklist/permission-scope-order';
import { UsersManagementService } from '../users-management.service';
import { forkJoin, of } from 'rxjs';
import { PermissionService } from '../permission.service';

@Component({
  selector: 'sc-role-edit',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    Dropdown,
    FormField,
    Input,
    ZagButton,
    ZagIcon,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
    AuditLogPanelComponent,
    PermissionChecklistComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './role-edit.component.html',
  styleUrl: './role-edit.component.scss',
})
export class RoleEditComponent {
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });
  private readonly rolesService = inject(RolesManagementService);
  private readonly permissionsService = inject(PermissionsManagementService);
  private readonly usersService = inject(UsersManagementService);
  private readonly permissionService = inject(PermissionService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly sidebarNavState = inject(SidebarNavStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Whether the audit log panel is open */
  readonly auditPanelOpen = signal(false);
  readonly canAccessAuditLog = computed(() => hasAuditLogAccess(this.permissionService));

  /** Route params as signal for reactive updates */
  private readonly routeParams = toSignal(this.route.paramMap);

  // Role ID from route (null for create mode) - reactive
  readonly roleId = computed(() => this.routeParams()?.get('id') ?? null);

  // Check if creating new
  readonly isNew = computed(() => !this.roleId());

  // Use the existing service's selected role
  private readonly selectedRole = this.rolesService.selectedRole;

  // Loading state
  readonly isLoading = computed(() => {
    if (this.isNew()) return false;
    return this.rolesService.isLoadingSelected();
  });

  // Track save state
  protected readonly isSaving = signal(false);

  // Error state
  readonly error = computed(() => {
    this.currentLang();
    if (this.isNew()) return null;
    const roleError = this.rolesService.error();
    if (roleError) {
      return this.translate.instant('settings.roles.loadError');
    }
    return null;
  });

  readonly form = new FormGroup({
    id: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^[a-z][a-z0-9_-]*$/)],
    }),
    description: new FormControl<string | null>(null),
  });

  /** All available permissions grouped by resource */
  readonly permissionsByResource = this.permissionsService.permissionsByResource;

  /** Flat permission list for the shared checklist component */
  readonly permissionsList = this.permissionsService.permissions;

  /** Checklist catalog wired to runtime scope metadata from GET /rbac/permissions. */
  readonly checklistPermissions = computed<PermissionChecklistItem[]>(() =>
    (this.permissionsList() as Array<Record<string, unknown>>).map(p => ({
      id: p['id'] as string,
      description: (p['description'] as string | null) ?? null,
      featureKey: (p['featureKey'] as string | null) ?? null,
      scopeEntityType: (p['scopeEntityType'] as PermissionChecklistItem['scopeEntityType']) ?? null,
      requiresPermissionId: (p['requiresPermissionId'] as string | null) ?? null,
    }))
  );

  /** Pending scope grants; row absence means inactive. */
  protected readonly selectedPermissionScopes = signal<PermissionScopeSelection>(new Map());

  // ============================================================================
  // Assigned users (direct + inherited via groups)
  // ============================================================================

  /** User IDs staged to add as direct assignments (not yet persisted). */
  private readonly _localAddedUserIds = signal<Set<string>>(new Set());

  /** User IDs staged to remove from direct assignments (not yet persisted). */
  private readonly _localRemovedUserIds = signal<Set<string>>(new Set());

  /** Dropdown pick buffer — cleared after each add. */
  protected readonly selectedUserToAdd = signal<string | null>(null);

  /** Server-side view of who currently holds this role. */
  readonly assignedUsers = this.rolesService.assignedUsers;
  readonly isLoadingAssignedUsers = this.rolesService.isLoadingAssignedUsers;

  /** All known users (from the users list); used to resolve added-user IDs to display data. */
  private readonly allUsers = this.usersService.users;

  /**
   * Effective direct-assignee list, merging server state with local staging:
   * backend direct users MINUS local-removed, PLUS local-added.
   */
  readonly effectiveDirectUsers = computed<(RoleUserSummary & { pending?: boolean })[]>(() => {
    const backend = this.assignedUsers().direct;
    const removed = this._localRemovedUserIds();
    const added = this._localAddedUserIds();

    const kept = backend.filter(u => !removed.has(u.id));
    const users = this.allUsers();
    const addedResolved: (RoleUserSummary & { pending?: boolean })[] = [];
    for (const id of added) {
      const u = users.find(x => x.id === id);
      if (u) {
        addedResolved.push({ id: u.id, name: u.name, email: u.email, image: u.image, pending: true });
      }
    }
    return [...kept, ...addedResolved];
  });

  readonly inheritedAssignedUsers = computed(() => this.assignedUsers().inherited);

  /** User IDs that hold the role directly or via a group — excluded from the "add" dropdown. */
  private readonly assignedUserIds = computed(() => {
    const backend = this.assignedUsers();
    const inheritedIds = backend.inherited.map(u => u.id);
    const directIds = backend.direct.map(u => u.id);
    return new Set<string>([...directIds, ...inheritedIds, ...this._localAddedUserIds()]);
  });

  /** Dropdown options for users who could be added directly. */
  readonly unassignedUserOptions = computed<DropdownOption<string>[]>(() => {
    const assigned = this.assignedUserIds();
    return this.allUsers()
      .filter(u => !assigned.has(u.id))
      .map(u => ({ label: u.email ? `${u.name} (${u.email})` : u.name, value: u.id }));
  });

  readonly hasUnsavedUserChanges = computed(() => this._localAddedUserIds().size > 0 || this._localRemovedUserIds().size > 0);

  /** Number of direct users after staging — surfaced as an accordion badge. */
  readonly assignedUsersBadge = computed(() => this.effectiveDirectUsers().length);

  /** Convert form values to signal for reactivity */
  private readonly formValues = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  /** Current role name for profile card */
  readonly roleName = computed(() => {
    this.currentLang();
    const id = this.roleId();
    if (id) {
      // Use form value if available (reflects current edits), otherwise backend data
      const formValues = this.formValues();
      if (formValues.id) return formValues.id;

      const role = this.selectedRole();
      return role?.id ?? this.translate.instant('settings.roles.loading');
    }
    return this.translate.instant('settings.roles.newRole');
  });

  /** Role icon for profile card */
  readonly roleIcon = computed(() => 'security');

  /** Profile subtitle */
  readonly roleSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.roles.entityType');
  });

  /** Stats for profile card (users and groups only - permissions shown in accordion badge) */
  readonly roleStats = computed<ProfileStat[]>(() => {
    this.currentLang();
    const role = this.selectedRole();
    if (!role) return [];

    return [
      {
        label: this.translate.instant('settings.roles.stats.users'),
        value: String(role.userCount ?? 0),
        icon: 'person',
      },
      {
        label: this.translate.instant('settings.roles.stats.groups'),
        value: String(role.groupCount ?? 0),
        icon: 'groups',
      },
    ];
  });

  /** Permissions count for accordion badge */
  readonly permissionsCount = computed(() => {
    return this.selectedPermissionScopes().size;
  });

  /** Danger zone actions for profile card */
  readonly dangerActions = computed<ProfileDangerAction[]>(() => {
    this.currentLang();
    const role = this.selectedRole();
    // Disable delete if role is in use
    const inUse = role && (role.userCount > 0 || role.groupCount > 0);

    return [
      {
        id: 'delete',
        label: this.translate.instant('common.delete'),
        icon: 'delete',
        disabled: this.isNew() || inUse,
      },
    ];
  });

  /** Quick actions for profile card */
  readonly quickActions = computed<ProfileQuickAction[]>(() => {
    this.currentLang();
    const isNewRole = this.isNew();
    if (!this.canAccessAuditLog()) {
      return [];
    }

    return [
      {
        id: 'audit-log',
        label: this.translate.instant('common.activityLog'),
        icon: 'history',
        disabled: isNewRole,
      },
    ];
  });

  /** Track previous ID to detect changes */
  private previousRoleId: string | null = null;

  constructor() {
    wireAuditPanelAutoOpen({
      isNew: this.isNew,
      canAccess: this.canAccessAuditLog,
      isSidebarCollapsed: this.sidebarNavState.isCollapsed,
      auditPanelOpen: this.auditPanelOpen,
      breakpointObserver: this.breakpointObserver,
      destroyRef: this.destroyRef,
      injector: this.injector,
    });

    // React to route param changes: reset form and load role data.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(() => {
      const currentId = this.roleId();
      if (currentId !== this.previousRoleId) {
        this.previousRoleId = currentId;
        this.form.reset({ id: '', description: null });
        this.selectedPermissionScopes.set(new Map());
        this._localAddedUserIds.set(new Set());
        this._localRemovedUserIds.set(new Set());
        this.selectedUserToAdd.set(null);

        if (currentId) {
          this.rolesService.selectById(currentId);
          this.rolesService.getPermissions(currentId).subscribe({
            next: res => {
              // Stored rows carry their scope; default to full when absent.
              this.selectedPermissionScopes.set(new Map(res.map(p => [p.id, (p as { scope?: PermissionScope }).scope ?? 'full'])));
            },
          });
        }
      }
    });

    // Populate form when role data loads.
    toObservable(this.selectedRole)
      .pipe(takeUntilDestroyed())
      .subscribe(role => {
        if (role && !this.isNew()) {
          this.form.patchValue({
            id: role.id,
            description: role.description,
          });
        }
      });
  }

  /** Handler for the shared PermissionChecklistComponent output. */
  protected onPermissionSelectionChange(scopes: PermissionScopeSelection): void {
    this.selectedPermissionScopes.set(scopes);
  }

  // ============================================================================
  // Assigned users — staging helpers
  // ============================================================================

  protected onAddUser(userId: string | null): void {
    if (!userId) return;

    // Sync the dropdown value into our signal FIRST so the null-reset below
    // actually fires the Dropdown's @Input setter (Angular setters only run on
    // reference change — null → null is a no-op and would leave stale internal
    // state that reappears when the user re-enters the options list after a
    // remove).
    this.selectedUserToAdd.set(userId);

    // If the user was staged for removal, undo that instead of adding.
    const removed = this._localRemovedUserIds();
    if (removed.has(userId)) {
      const next = new Set(removed);
      next.delete(userId);
      this._localRemovedUserIds.set(next);
    } else {
      const added = new Set(this._localAddedUserIds());
      added.add(userId);
      this._localAddedUserIds.set(added);
    }

    // Reset on the next macrotask so Angular CD flushes the userId setter call
    // before we clear it.
    setTimeout(() => this.selectedUserToAdd.set(null), 0);
  }

  protected removeAssignedUser(userId: string): void {
    // If the user was a local addition, pop it. Otherwise mark for backend removal.
    const added = this._localAddedUserIds();
    if (added.has(userId)) {
      const next = new Set(added);
      next.delete(userId);
      this._localAddedUserIds.set(next);
    } else {
      const removed = new Set(this._localRemovedUserIds());
      removed.add(userId);
      this._localRemovedUserIds.set(removed);
    }

    // Force the dropdown value to null via the userId → null transition so the
    // Dropdown's internal selectedValue doesn't hold a stale id that would
    // reappear as the display label when the removed user re-enters options.
    this.selectedUserToAdd.set(userId);
    setTimeout(() => this.selectedUserToAdd.set(null), 0);
  }

  protected isLocalUserAddition(userId: string): boolean {
    return this._localAddedUserIds().has(userId);
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const grants = Array.from(this.selectedPermissionScopes(), ([permissionId, scope]) => ({ permissionId, scope }));

    if (this.isNew()) {
      // Create new role
      this.rolesService
        .create({
          id: this.form.controls.id.value,
          description: this.form.controls.description.value ?? undefined,
        })
        .subscribe({
          next: res => {
            this.rolesService.updatePermissionsWithScopes(res.id, grants).subscribe({
              next: () => {
                this.isSaving.set(false);
                this.toast.success(this.translate.instant('settings.roles.saveSuccess'));
                this.rolesService.reload();
                this.router.navigate(['/settings/roles', res.id]);
              },
              error: (err: { error?: { message?: string } }) => {
                this.isSaving.set(false);
                this.toast.error(err?.error?.message || this.translate.instant('settings.roles.saveError'));
              },
            });
          },
          error: (err: { error?: { message?: string } }) => {
            this.isSaving.set(false);
            this.toast.error(err?.error?.message || this.translate.instant('settings.roles.saveError'));
          },
        });
    } else {
      const roleId = this.roleId();
      if (!roleId) return;

      const added = Array.from(this._localAddedUserIds());
      const removed = Array.from(this._localRemovedUserIds());

      const userMutations = [
        ...added.map(userId => this.rolesService.assignUserToRole(roleId, userId)),
        ...removed.map(userId => this.rolesService.unassignUserFromRole(roleId, userId)),
      ];

      forkJoin({
        updateRole: this.rolesService.update(roleId, {
          description: this.form.controls.description.value ?? undefined,
        }),
        updatePermissions: this.rolesService.updatePermissionsWithScopes(roleId, grants),
        users: userMutations.length > 0 ? forkJoin(userMutations) : of([]),
      }).subscribe({
        next: () => {
          this.isSaving.set(false);
          this.toast.success(this.translate.instant('settings.roles.saveSuccess'));
          this._localAddedUserIds.set(new Set());
          this._localRemovedUserIds.set(new Set());
          this.rolesService.reload();
          this.rolesService.reloadAssignedUsers();
          this.usersService.reload();
        },
        error: (err: { error?: { message?: string } }) => {
          this.isSaving.set(false);
          this.toast.error(err?.error?.message || this.translate.instant('settings.roles.users.partialSaveError'));
          // Reload assigned users so UI reflects whatever partially succeeded.
          this.rolesService.reloadAssignedUsers();
        },
      });
    }
  }

  protected async onCancel(): Promise<void> {
    await this.router.navigate(['/settings/roles']);
  }

  /** Handle quick action click */
  protected onQuickAction(action: ProfileQuickAction): void {
    if (action.id === 'audit-log') {
      this.auditPanelOpen.set(!this.auditPanelOpen());
    }
  }

  /** Handle activity log click */
  protected onActivityLog(): void {
    this.auditPanelOpen.set(!this.auditPanelOpen());
  }

  protected onDangerAction(action: { id: string }): void {
    if (action.id === 'delete') {
      this.onDelete();
    }
  }

  private async onDelete(): Promise<void> {
    const roleId = this.roleId();
    const role = this.selectedRole();
    if (!roleId || !role) return;

    const confirmMessage = this.translate.instant('settings.roles.deleteConfirm', {
      id: role.id,
    });
    const confirmed = await this.confirmDialog.confirm({
      title: this.translate.instant('common.delete'),
      message: confirmMessage,
      confirmText: this.translate.instant('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;

    this.rolesService.delete(roleId).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('settings.roles.deleteSuccess'));
        this.router.navigate(['/settings/roles']);
      },
      error: (err: { error?: { message?: string } }) => {
        this.toast.error(err?.error?.message || this.translate.instant('settings.roles.deleteError'));
      },
    });
  }

  protected getFieldError(field: keyof typeof this.form.controls): string | null {
    return getFieldErrorUtil(this.form.controls[field], this.translate);
  }
}
